/**
 * NBPLCFinancing — Financials Page
 * Route: /corporate/nbplc/financing
 *
 * Data: useProjectStore() — SSOT, unchanged.
 * Layout: Portfolio Snapshot → Portfolio Value → Top 5 → Full Table (collapsed)
 */
import React, { useState, useMemo } from "react";
import { useProjectStore } from "@/store/projectStore";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const fmtV = (v) => v >= 1_000_000 ? `$${(v/1e6).toFixed(1)}M` : v >= 100_000 ? `$${(v/1e6).toFixed(2)}M` : `$${Math.round(v/1000)}K`;
const fmtK = (v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : `${Math.round(v)}`;

function ProjectRow({ o }) {
  return (
    <tr className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
      <td className="py-2.5 pr-3 font-semibold text-slate-900 max-w-[180px] truncate">{o.name}</td>
      <td className="py-2.5 pr-3">
        {o.cpa_group ? (
          <div>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
              style={{ background: o.cpa_group.color + "22", color: o.cpa_group.color }}>
              {o.cpa_group.id}
            </span>
            <p className="text-[9px] text-slate-400 mt-0.5">{o.cpa_group.label}</p>
          </div>
        ) : <span className="text-[9px] text-slate-400">Internal</span>}
      </td>
      <td className="py-2.5 pr-3 tabular-nums">{fmtV(o.capex_usd || 0)}</td>
      <td className="py-2.5 pr-3 font-bold text-teal-700 tabular-nums">{fmtV(o.cost_savings_usd_yr || 0)}/yr</td>
      <td className="py-2.5 pr-3 font-semibold text-emerald-700">{o.irr_pct || 0}%</td>
      <td className="py-2.5 pr-3 tabular-nums">{(o.payback_years || 0).toFixed(1)}yr</td>
      <td className={`py-2.5 pr-3 ${o.strength?.cls || ""}`}>{o.strength?.label || "—"}</td>
      <td className="py-2.5 pr-3">
        <p className={`text-[10px] font-semibold leading-tight ${o.additionality?.cls || ""}`}>
          {o.additionality?.label || "—"}
        </p>
      </td>
      <td className="py-2.5">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
          ["structuring_in_progress","structuring_ready"].includes(o.status) ? "bg-amber-100 text-amber-700 border-amber-200" :
          o.status === "financing_review" ? "bg-purple-100 text-purple-700 border-purple-200" :
          o.status === "promoted_to_strategy" ? "bg-blue-100 text-blue-700 border-blue-200" :
          "bg-slate-100 text-slate-500 border-slate-200"
        }`}>{o.status.replace(/_/g," ")}</span>
      </td>
    </tr>
  );
}

export default function NBPLCFinancing() {
  const {
    period, evidencePct, vbLoading,
    projects, portfolio, vcmProjects, art6Projects, vbData,
  } = useProjectStore();

  const [showFullTable, setShowFullTable] = useState(false);
  const loading = vbLoading && !vbData;

  const portfolioOpps = useMemo(() =>
    [...projects].sort((a,b) => (b.composite_score||0) - (a.composite_score||0)),
  [projects]);

  const top5       = portfolioOpps.slice(0, 5);
  const vcmRed     = vcmProjects.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);
  const art6Red    = art6Projects.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);
  const cBaseVcm   = Math.round(vcmRed * 10 / 10000) * 10000;

  const confidence = (evidencePct||60) >= 70
    ? { label:"High",   cls:"bg-emerald-50 text-emerald-700 border-emerald-200" }
    : (evidencePct||60) >= 30
    ? { label:"Medium", cls:"bg-amber-50 text-amber-700 border-amber-200" }
    : { label:"Low",    cls:"bg-red-50 text-red-700 border-red-200" };

  const TABLE_HEADERS = [
    "Project","CPA Group","CAPEX","A. Savings/yr",
    "IRR","Payback","Strength","Additionality (Prelim.)","Status"
  ];

  if (loading) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-7">

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
        <PageHeader title="Financials" />
        <p className="text-xs text-slate-400 mt-1">
          {period?.label} · Carbon Structuring → Portfolio → Monetization
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${confidence.cls}`}>
            {confidence.label} Confidence · Evidence {evidencePct||60}%
          </span>
          <span className="text-[10px] text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">
            {portfolioOpps.length} projects · {vcmProjects.length} VCM-eligible · {art6Projects.length} Art.6-eligible
          </span>
        </div>
      </div>

      {/* ── SECTION 1: PORTFOLIO SNAPSHOT ──────────────────────────────── */}
      <div>
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Portfolio Snapshot</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="nb-financials-summary">
          <MetricCard label="Total CAPEX"        value={fmtV(portfolio.totalCap)}     color="text-amber-600"  sub="full pipeline" />
          <MetricCard label="A. Annual Savings"  value={fmtV(portfolio.opValue)} unit="/yr" color="text-teal-700" sub="bankable — no carbon" />
          <MetricCard label="Total Reduction"    value={fmtK(portfolio.totalRed)} unit="tCO₂e/yr" color="text-emerald-700" sub={`${portfolioOpps.length} opportunities`} />
          <MetricCard label="Blended Payback"    value={portfolio.blended}             color="text-slate-700"  sub="savings-based" />
        </div>
      </div>

      {/* ── SECTION 2: PORTFOLIO VALUE (A/B) ────────────────────────────── */}
      <div>
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Portfolio Value</p>
        <div className="grid grid-cols-2 gap-4">
          {/* A — Operational */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
            <p className="text-[9px] font-bold text-emerald-700 uppercase tracking-wide mb-1">A. Operational Value</p>
            <p className="text-3xl font-black text-emerald-800 mb-3">{fmtV(portfolio.opValue)}<span className="text-base font-normal">/yr</span></p>
            <div className="space-y-1 pt-3 border-t border-emerald-200 text-xs">
              <div className="flex justify-between">
                <span className="text-emerald-600">Energy savings</span>
                <span className="font-semibold text-emerald-800">{fmtV(portfolio.totalSav)}/yr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-emerald-600">Circularity value</span>
                <span className="font-semibold text-emerald-800">{fmtV(portfolio.totalCirc)}/yr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-emerald-600">Total CAPEX</span>
                <span className="font-semibold text-emerald-800">{fmtV(portfolio.totalCap)}</span>
              </div>
            </div>
            <p className="text-[9px] text-emerald-700 font-bold mt-3 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"/>
              DSCR-eligible. No validation required.
            </p>
          </div>

          {/* B — Carbon (Indicative) */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1">B. Carbon Value (Indicative)</p>
            <p className="text-3xl font-black text-slate-400 mb-1">{fmtV(cBaseVcm)}<span className="text-base font-normal text-slate-400">/yr</span></p>
            <p className="text-[10px] text-slate-400 mb-3">VCM base $10/t · {vcmProjects.length} projects</p>

            <div className="space-y-1 text-xs">
              {[["VCM Low $5/t", fmtV(Math.round(vcmRed*5/10000)*10000)],
                ["VCM Base $10/t", fmtV(Math.round(vcmRed*10/10000)*10000)],
                ["Art.6 Base $20/t", fmtV(Math.round(art6Red*20/10000)*10000)],
                ["Art.6 High $40/t", fmtV(Math.round(art6Red*40/10000)*10000)],
              ].map(([label,val]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-slate-400 italic">{label}</span>
                  <span className="text-slate-500 italic">{val}/yr</span>
                </div>
              ))}
            </div>

            <p className="text-[9px] text-red-500 font-bold mt-3 flex items-start gap-1.5">
              <AlertTriangle size={9} className="flex-shrink-0 mt-0.5"/>
              Not in DSCR until VVB validation (VCM) or LoA (Article 6) confirmed.
            </p>
          </div>
        </div>
      </div>

      {/* ── SECTION 3: TOP 5 PROJECTS ────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Project Portfolio — Top 5</p>
          <p className="text-[10px] text-slate-400">Ranked by composite score · Section A (Operational) only</p>
        </div>
        <SectionCard>
          <table className="w-full text-xs" data-testid="nb-financials-table">
            <thead>
              <tr className="border-b border-slate-100">
                {TABLE_HEADERS.map(h => (
                  <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {top5.map(o => <ProjectRow key={o.opp_id} o={o} />)}
            </tbody>
          </table>
          <p className="text-[9px] text-slate-400 mt-2 italic">
            Strength: Strong = IRR &gt;20% · Moderate = 12–20% · Weak = &lt;12%
          </p>

          {/* Expand button */}
          <button
            onClick={() => setShowFullTable(v => !v)}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:border-teal-300 hover:text-teal-700 transition-colors"
            data-testid="view-full-portfolio-btn">
            {showFullTable
              ? <><ChevronUp size={13}/> Collapse portfolio</>
              : <><ChevronDown size={13}/> View Full Portfolio ({portfolioOpps.length} projects)</>}
          </button>
        </SectionCard>
      </div>

      {/* ── SECTION 4: FULL TABLE (collapsed by default) ─────────────────── */}
      {showFullTable && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Full Portfolio Table</p>
            <span className="text-[10px] text-slate-400">{portfolioOpps.length} projects total</span>
          </div>
          <SectionCard>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  {TABLE_HEADERS.map(h => (
                    <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {portfolioOpps.map(o => <ProjectRow key={o.opp_id} o={o} />)}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td className="py-2.5 pr-3 font-bold text-slate-900">PORTFOLIO TOTAL</td>
                  <td className="py-2.5 pr-3 text-[9px] text-slate-400">{portfolioOpps.length} projects</td>
                  <td className="py-2.5 pr-3 font-bold text-amber-700 tabular-nums">{fmtV(portfolio.totalCap)}</td>
                  <td className="py-2.5 pr-3 font-bold text-teal-700 tabular-nums">{fmtV(portfolio.opValue)}/yr</td>
                  <td className="py-2.5 pr-3 font-bold text-emerald-700">
                    {(portfolioOpps.reduce((s,o)=>s+(o.irr_pct||0),0)/Math.max(portfolioOpps.length,1)).toFixed(1)}%
                  </td>
                  <td className="py-2.5 pr-3 font-bold">{portfolio.blended}</td>
                  <td colSpan={3} className="py-2.5 text-[9px] text-slate-400">
                    Blended · Section A only · DSCR = savings ÷ debt service
                  </td>
                </tr>
              </tfoot>
            </table>
          </SectionCard>
        </div>
      )}

      {/* ── MANDATORY DISCLAIMER ─────────────────────────────────────────── */}
      <div className="bg-red-50 border-l-4 border-red-500 rounded-xl px-4 py-3">
        <p className="text-xs font-bold text-red-700 mb-1">Mandatory Notice</p>
        <p className="text-xs text-red-700 font-semibold">Financials based on operational savings only — carbon value excluded.</p>
        <p className="text-xs text-red-600 mt-1">
          No carbon revenue is included in IRR, payback, or savings calculations.
          Carbon value (Section B) requires VVB validation (VCM) or host country authorization —
          LoA (Article 6) before recognition. Indicative only. Independent due diligence required.
        </p>
      </div>
    </div>
  );
}
